/**
 * ISO-8601 week identity (`2026-W33`) for a `YYYY-MM-DD` service date.
 *
 * ⚠ WHAT IS LEFT OF `generation-key.ts`. That file derived `cycleId`,
 * `countLockId` and `templateVersion` in the browser because CORE-05A's Friday
 * 7:00 PM count lock did not exist (OQ-66, OQ-70). It does now, the server
 * resolves all three from `therapist_final_counts`, and the derivation is gone
 * — `packages/logic/rider-session-keys.ts` holds the one that survived.
 *
 * The week label stayed because it is not a key: it is the column that tells an
 * operator which cycle a batch belongs to.
 *
 * Computed in UTC from the date parts, NOT from a local `new Date(str)`: a bare
 * date string is parsed as UTC midnight, and any timezone arithmetic on top of
 * that can slide a Sunday into the previous week. The operator's browser
 * timezone must not decide which cycle a Saturday belongs to.
 */
export function isoWeekOf(serviceDate: string): string {
  const [y, m, d] = serviceDate.split("-").map(Number);
  if (!y || !m || !d) throw new Error(`Not a YYYY-MM-DD date: ${serviceDate}`);
  const date = new Date(Date.UTC(y, m - 1, d));

  // ISO weeks start Monday and belong to the year containing their Thursday.
  // Shift to that Thursday, then count weeks from the year's first Thursday.
  const dayIndex = (date.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  date.setUTCDate(date.getUTCDate() - dayIndex + 3);
  const isoYear = date.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const firstDayIndex = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayIndex + 3);
  const week = 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
  return `${isoYear}-W${String(week).padStart(2, "0")}`;
}

/** A complete `YYYY-MM-DD` value — what a cleared date input is not. */
export function isServiceDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}
